import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";

interface BlockedUser {
  id: string;
  blocked_id: string;
  created_at: string;
  reason: string | null;
  profile: {
    id: string;
    username: string;
    full_name: string | null;
    avatar_url: string | null;
  } | null;
}

export function useBlockedUsers() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // Fetch all users the current user has blocked
  const { data: blockedUsers = [], isLoading } = useQuery({
    queryKey: ["blocked-users", user?.id],
    queryFn: async () => {
      if (!user) return [];

      const { data: blocks, error } = await supabase
        .from("user_blocks")
        .select("id, blocked_id, created_at, reason")
        .eq("blocker_id", user.id);

      if (error) throw error;
      if (!blocks || blocks.length === 0) return [];

      // Fetch profiles for blocked users
      const blockedIds = blocks.map((b) => b.blocked_id);
      const { data: profiles } = await supabase
        .rpc("get_public_profiles_by_ids", { user_ids: blockedIds });

      return blocks.map((block) => ({
        ...block,
        profile: profiles?.find((p: { id: string }) => p.id === block.blocked_id) || null,
      })) as BlockedUser[];
    },
    enabled: !!user,
  });

  // Check if a specific user is blocked
  const isUserBlocked = (userId: string) => {
    return blockedUsers.some((b) => b.blocked_id === userId);
  };

  // Check if you are blocked by a specific user
  const useIsBlockedBy = (userId: string | undefined) => {
    return useQuery({
      queryKey: ["is-blocked-by", user?.id, userId],
      queryFn: async () => {
        if (!user || !userId) return false;

        const { data, error } = await supabase
          .from("user_blocks")
          .select("id")
          .eq("blocker_id", userId)
          .eq("blocked_id", user.id)
          .maybeSingle();

        if (error) throw error;
        return !!data;
      },
      enabled: !!user && !!userId,
    });
  };

  // Block a user
  const blockUser = useMutation({
    mutationFn: async ({ userId, reason }: { userId: string; reason?: string }) => {
      if (!user) throw new Error("Not authenticated");

      // First, remove any existing friendship
      await supabase
        .from("user_connections")
        .delete()
        .or(`and(user_id.eq.${user.id},connected_user_id.eq.${userId}),and(user_id.eq.${userId},connected_user_id.eq.${user.id})`);

      // Then create the block
      const { error } = await supabase
        .from("user_blocks")
        .insert({
          blocker_id: user.id,
          blocked_id: userId,
          reason: reason || null,
        });

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["blocked-users"] });
      queryClient.invalidateQueries({ queryKey: ["friends"] });
      queryClient.invalidateQueries({ queryKey: ["user-connections"] });
      queryClient.invalidateQueries({ queryKey: ["connection-status"] });
      toast.success("User blocked");
    },
    onError: (error) => {
      console.error("Error blocking user:", error);
      toast.error("Failed to block user");
    },
  });

  // Unblock a user
  const unblockUser = useMutation({
    mutationFn: async (userId: string) => {
      if (!user) throw new Error("Not authenticated");

      const { error } = await supabase
        .from("user_blocks")
        .delete()
        .eq("blocker_id", user.id)
        .eq("blocked_id", userId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["blocked-users"] });
      toast.success("User unblocked");
    },
    onError: (error) => {
      console.error("Error unblocking user:", error);
      toast.error("Failed to unblock user");
    },
  });

  return {
    blockedUsers,
    isLoading,
    isUserBlocked,
    useIsBlockedBy,
    blockUser,
    unblockUser,
  };
}
